import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../utils/supabaseClient';
import {
  UsersIcon,
  ReceiptPercentIcon,
  BoltIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';

/**
 * Picks the setup path FOR the contractor instead of making them guess.
 *
 * Onboarding used to open on a two-card choice — "Quick Start" or "Complete Setup" — which
 * asks somebody who has never seen the product to predict which one they need. They cannot,
 * so the choice is either a coin flip or the shorter one every time.
 *
 * Two questions instead, and each maps to a step the full flow actually adds:
 *   a team          -> Team Setup (invites)
 *   billing details -> Financial Setup (tax rate, invoice numbering, payment terms)
 * Business hours, the third full-only step, is deliberately NOT asked: company_settings
 * ships a sensible Mon–Fri 08:00–17:00 default that every one of the 186 live companies
 * already carries, so asking would add a question that changes nothing.
 *
 * The recommendation is shown, not imposed — the contractor can take the other path in one
 * click. Routed, not trapped.
 *
 * Answers persist to company_settings.onboarding_progress.assessment, which is where this
 * component always wrote them; the difference is that something reads them now.
 */
const AssessmentStep = ({ onChoose, onSkip }) => {
  const { user } = useUser();
  const [assessment, setAssessment] = useState({
    has_employees: false,
    has_billing_details: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user?.company_id) return undefined;
    supabase
      .from('company_settings')
      .select('onboarding_progress')
      .eq('company_id', user.company_id)
      .maybeSingle()
      .then(({ data }) => {
        const prior = data?.onboarding_progress?.assessment;
        if (!cancelled && prior) {
          setAssessment(a => ({ ...a, ...prior }));
        }
      });
    return () => { cancelled = true; };
  }, [user?.company_id]);

  // Either "yes" means there is real work the quick flow does not cover.
  const recommended = (assessment.has_employees || assessment.has_billing_details)
    ? 'complete'
    : 'quick';

  const start = async (mode) => {
    setSaving(true);
    // Recording the answers is best-effort: a contractor must never be blocked from
    // starting setup because a preferences write failed.
    try {
      if (user?.company_id) {
        const { data } = await supabase
          .from('company_settings')
          .select('onboarding_progress')
          .eq('company_id', user.company_id)
          .maybeSingle();

        await supabase
          .from('company_settings')
          .update({
            onboarding_progress: {
              ...(data?.onboarding_progress || {}),
              assessment: { ...assessment, chosen_mode: mode }
            },
            updated_at: new Date().toISOString()
          })
          .eq('company_id', user.company_id);
      }
    } catch (err) {
      console.warn('Could not save setup answers (non-blocking):', err?.message || err);
    }
    onChoose?.(mode);
  };

  const Question = ({ icon: Icon, title, detail, value, onToggle }) => (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full text-left rounded-xl border p-4 transition-colors ${
        value
          ? 'border-blue-400 bg-blue-500/10 dark:bg-blue-500/15'
          : 'border-slate-700/40 bg-slate-800/40 hover:border-slate-600'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          value ? 'bg-blue-500/20 text-blue-300' : 'bg-slate-700/40 text-slate-400'
        }`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-white">{title}</div>
          <div className="text-sm text-slate-400 mt-0.5">{detail}</div>
        </div>
        <div className={`ml-auto shrink-0 w-5 h-5 rounded border flex items-center justify-center ${
          value ? 'bg-blue-500 border-blue-500' : 'border-slate-600'
        }`}>
          {value && <span className="text-white text-xs leading-none">✓</span>}
        </div>
      </div>
    </button>
  );

  return (
    <div className="max-w-xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold text-white">Let's get you set up</h2>
        <p className="text-slate-400 mt-1">Two questions, so we only ask for what you need.</p>
      </div>

      <div className="space-y-3">
        <Question
          icon={UsersIcon}
          title="Do you have a team?"
          detail="Employees or subs you'll schedule work for"
          value={assessment.has_employees}
          onToggle={() => setAssessment(a => ({ ...a, has_employees: !a.has_employees }))}
        />
        <Question
          icon={ReceiptPercentIcon}
          title="Want to set up billing now?"
          detail="Tax rate, payment terms and invoice numbering"
          value={assessment.has_billing_details}
          onToggle={() => setAssessment(a => ({ ...a, has_billing_details: !a.has_billing_details }))}
        />
      </div>

      <div className="mt-6 rounded-xl border border-slate-700/40 bg-slate-800/30 p-4">
        <div className="flex items-center gap-2 text-sm text-slate-300">
          {recommended === 'quick'
            ? <BoltIcon className="w-4 h-4 text-amber-400" />
            : <ClipboardDocumentCheckIcon className="w-4 h-4 text-blue-400" />}
          {recommended === 'quick'
            ? 'Quick Start — 3 steps. You can add the rest from Settings any time.'
            : 'Full Setup — 6 steps, including the bits you just told us about.'}
        </div>
      </div>

      <button
        type="button"
        onClick={() => start(recommended)}
        disabled={saving}
        className="mt-4 w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60"
      >
        {saving ? 'Starting…' : recommended === 'quick' ? 'Start Quick Setup' : 'Start Full Setup'}
      </button>

      <div className="mt-3 flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={() => start(recommended === 'quick' ? 'complete' : 'quick')}
          disabled={saving}
          className="text-slate-400 hover:text-white"
        >
          {recommended === 'quick' ? 'Do the full setup instead' : 'Just do the essentials'}
        </button>
        {onSkip && (
          <button type="button" onClick={onSkip} className="text-slate-500 hover:text-slate-300">
            Skip for now
          </button>
        )}
      </div>
    </div>
  );
};

export default AssessmentStep;
